import * as R from 'ramda';
import { createApiActionTypes } from 'iqvia-shared';
import { v4 as uuidv4 } from 'uuid';

import {
  DEFAULT_PREVIEW_FILE_MAX_RECORD_COUNT,
  getInArgumentsProperties,
  PATHS_CAPACITY,
} from '../constants/JourneyBuilder';
import { getIntl } from '../../Shared/localization';
import { types } from '../actions/JourneyBuilder';
import apiNameEnums from '../../Enums/apiNames';

export const defaultState = {
  decisionSplitObjects: [],
  error: null,
  isLoading: false,
  isInitializeProcess: true,
  isLoadingLocalizationData: true,
  isValidatingJourneyMetadata: true,
  isLoadingDecisionSplitUserData: true,
  isLoadingDecisionSplitObjects: true,
  isLoadingDecisionSplitObjectFields: true,
  hasChannelCode: false,
  requestedStepNumber: 0,
  currentStepIndex: 0,
  journeyHasMetadata: false,
  payload: {},
  JourneyBuilder: { maxRecordCount: DEFAULT_PREVIEW_FILE_MAX_RECORD_COUNT },
  readonlyForm: false,
  culture: null,
  localeInfo: {
    locale: null,
    messages: {},
  },
  currentMID: 0,
  tokens: null,
  journeyId: null,
  topicName: null,
  isHCO: false,
};

export const findValue = (propertyName, inArguments = []) => {
  const argument = inArguments.find(value => value[propertyName]);
  return (argument && argument[propertyName]) || null;
};

export const getValuesFromPayload = (payload, journeyBuilderTimezone, isSocialMessage) => {
  const journeyBuilderValues = {};
  const inArgumentsProperties = getInArgumentsProperties(isSocialMessage);
  for (let i = 0; i < inArgumentsProperties.length; i++) {
    if (inArgumentsProperties[i] === 'timezone' && journeyBuilderTimezone) {
      journeyBuilderValues.timezone = journeyBuilderTimezone;
    } else {
      journeyBuilderValues[inArgumentsProperties[i]] = findValue(
        inArgumentsProperties[i],
        payload.arguments.execute.inArguments
      );
    }
  }
  journeyBuilderValues['name'] = payload.name;

  return journeyBuilderValues;
};

export const getIsLoading = ({
  isLoading,
  isInitializeProcess,
  isLoadingLocalizationData,
  isValidatingJourneyMetadata,
  isLoadingDecisionSplitUserData,
  isLoadingDecisionSplitObjects,
}) =>
  isLoading ||
  isInitializeProcess ||
  isLoadingLocalizationData ||
  isValidatingJourneyMetadata ||
  isLoadingDecisionSplitUserData ||
  isLoadingDecisionSplitObjects;

export const checkIsDisabledDoneBtn = state =>
  getIsLoading(state) || state.isLoadingDecisionSplitObjectFields;

export const fixPathNameRepetition = (name, outcomes) => {
  const item = outcomes.find(x => x.metaData.label === name);
  return item ? fixPathNameRepetition(`${name}(2)`, outcomes) : name;
};

export const getNextDecisionPathName = outcomes =>
  fixPathNameRepetition(
    `${getIntl().formatMessage({
      id: 'journeyBuilder.decisionSplit_decision',
    })} ${outcomes.length}`,
    outcomes
  );

export const addPath = outcomes => {
  if (outcomes.length === PATHS_CAPACITY) {
    throw new Error('path limit exceeded');
  }
  const [last, ...rest] = [...outcomes].reverse();
  const index = uuidv4();
  return [
    ...rest.reverse(),
    {
      arguments: {
        branchResult: index,
      },
      metaData: {
        label: getNextDecisionPathName(outcomes),
        fieldValues: rest.length > 0 && rest[rest.length - 1].metaData.fieldValues,
        invalid: rest.length > 0 && rest[rest.length - 1].metaData.invalid,
        objectValue: rest.length > 0 && rest[rest.length - 1].metaData.objectValue,
        contactType: rest.length > 0 && rest[rest.length - 1].metaData.contactType,
      },
    },
    last,
  ];
};

export const setPathObject = (outcomes, index, newValue) => [
  ...outcomes.slice(0, index),
  {
    ...outcomes[index],
    metaData: { ...outcomes[index].metaData, objectValue: newValue },
  },
  ...outcomes.slice(index + 1),
];

export const setPathContactType = (outcomes, index, newValue) => [
  ...outcomes.slice(0, index),
  {
    ...outcomes[index],
    metaData: { ...outcomes[index].metaData, contactType: newValue },
  },
  ...outcomes.slice(index + 1),
];

export const setPathObjectField = (outcomes, index, fieldName, newValue) => {
  const newFieldValues = { ...outcomes[index].metaData.fieldValues };
  newFieldValues[`${fieldName}`] = newValue;

  return [
    ...outcomes.slice(0, index),
    {
      ...outcomes[index],
      metaData: {
        ...outcomes[index].metaData,
        fieldValues: newFieldValues,
      },
    },
    ...outcomes.slice(index + 1),
  ];
};

export const removePath = (outcomes, index) => {
  if (outcomes.length - 1 === index) {
    throw new Error('cannot remove the last path');
  }
  if (index < 0 || index > outcomes.length - 1) {
    throw new Error('index is out of bounds');
  }
  return [...outcomes.slice(0, index), ...outcomes.slice(index + 1)];
};

export const renamePath = (outcomes, index, newValue) => [
  ...outcomes.slice(0, index),
  {
    ...outcomes[index],
    metaData: { ...outcomes[index].metaData, label: newValue },
  },
  ...outcomes.slice(index + 1),
];

export const cleanPayload = payload => {
  const newExecute = {};
  if (payload.data.arguments.execute) {
    Object.keys(payload.data.arguments.execute).forEach(key => {
      newExecute[key.substring(0, 1).toLowerCase() + key.substring(1)] =
        payload.data.arguments.execute[key];
    });
  }
  payload.data.arguments.execute = newExecute;
  const inArguments = R.path(['data', 'arguments', 'execute', 'inArguments'], payload);
  if (!inArguments) {
    payload.data.arguments.execute.inArguments = [];
    return payload;
  }
  const dates = inArguments.find(item => item['scheduleDates'] !== undefined);
  if (dates !== undefined) {
    dates.scheduleDates = Array.isArray(dates.scheduleDates)
      ? dates.scheduleDates
      : !dates.scheduleDates || dates.scheduleDates.length < 1
        ? []
        : dates.scheduleDates.split(',');
  }
  return payload;
};

export const cleanInitialOutcomes = outcomes =>
  outcomes &&
  outcomes.map(
    (x, i) =>
      x.metaData && x.metaData.label
        ? x
        : {
            ...x,
            metaData: {
              ...x.metaData,
              label:
                i === outcomes.length - 1
                  ? `${getIntl().formatMessage({
                      id: 'journeyBuilder.decisionSplit_remaining',
                    })}`
                  : `${getIntl().formatMessage({
                      id: 'journeyBuilder.decisionSplit_decision',
                    })} ${i + 1}`,
            },
          }
  );

export const cleanObjectFields = fields =>
  fields &&
  fields.map(x => ({
    name: x.name,
    criteria: JSON.parse(x.criteria),
    code: x.code,
  }));

export const getIndex = (state, payload) => {
  const currentObject = state.decisionSplitObjects.find(x => x.id === payload.objectId);
  const fieldIndex = currentObject.fields.indexOf(payload.field);
  return fieldIndex;
};

export const journeyBuilderGetMetadataActionTypes = createApiActionTypes(
  apiNameEnums.JOURNEY_BUILDER_JOURNEY_GET_METADATA
);

export const journeyBuilderGetHCOEnabledTypes = createApiActionTypes(
  apiNameEnums.JOURNEY_BUILDER_JOURNEY_GET_HCO_ENABLED
);

export const journeyBuilderDecisionSplitGetObjectFieldsActionTypes = createApiActionTypes(
  apiNameEnums.JOURNEY_BUILDER_DECISION_SPLIT_GET_OBJECT_FIELDS
);

export const journeyBuilderDecisionSplitGetDynamicListObjectFieldsActionTypes = createApiActionTypes(
  apiNameEnums.JOURNEY_BUILDER_DECISION_SPLIT_GET_DYNAMIC_LIST_OBJECT_FIELDS
);

export const journeyBuilderDecisionSplitGetUserDataActionTypes = createApiActionTypes(
  apiNameEnums.JOURNEY_BUILDER_DECISION_SPLIT_GET_USER_DATA
);

export const journeyBuilderDecisionSplitGetObjectsActionTypes = createApiActionTypes(
  apiNameEnums.JOURNEY_BUILDER_DECISION_SPLIT_GET_OBJECTS
);

export const journeyBuilderGetLocalizationActionTypes = createApiActionTypes(
  apiNameEnums.JOURNEY_BUILDER_GET_LOCALIZATION
);

export default (state = defaultState, { type, payload }) => {
  switch (type) {
    case types.JOURNEY_BUILDER_SAVE_JOURNEY_ID:
      return {
        ...state,
        journeyId: payload.id,
      };
    case types.JOURNEY_BUILDER_SET_TOKEN:
      return {
        ...state,
        tokens: payload.tokens,
      };
    case types.JOURNEY_BUILDER_PAYLOAD_ADD_PATH:
      return {
        ...state,
        payload: { ...state.payload, outcomes: addPath(state.payload.outcomes) },
      };
    case types.JOURNEY_BUILDER_PAYLOAD_REMOVE_PATH:
      return {
        ...state,
        payload: {
          ...state.payload,
          outcomes: removePath(state.payload.outcomes, payload.index),
        },
      };
    case types.JOURNEY_BUILDER_PAYLOAD_SET_PATH_OBJECT:
      return {
        ...state,
        payload: {
          ...state.payload,
          outcomes: setPathObject(state.payload.outcomes, payload.index, payload.newValue),
        },
      };
    case types.JOURNEY_BUILDER_PAYLOAD_SET_PATH_CONTACTTYPE:
      return {
        ...state,
        payload: {
          ...state.payload,
          outcomes: setPathContactType(state.payload.outcomes, payload.index, payload.newValue),
        },
      };
    case types.SET_FORM_READONLY:
      return {
        ...state,
        readonlyForm: true,
      };
    case types.JOURNEY_BUILDER_REQUESTING_NEW_STEP:
      return {
        ...state,
        requestedStepNumber: state.currentStepIndex + 1,
      };
    case types.JOURNEY_BUILDER_PAYLOAD_SET_PATH_OBJECT_FIELD:
      return {
        ...state,
        payload: {
          ...state.payload,
          outcomes: setPathObjectField(
            state.payload.outcomes,
            payload.index,
            payload.fieldName,
            payload.newValue
          ),
        },
      };
    case types.JOURNEY_BUILDER_PAYLOAD_FETCH_SUCCESS:
      payload = cleanPayload(payload);
      return {
        ...state,
        isInitializeProcess: false,
        payload: payload.data,
        hasChannelCode: Boolean(
          payload.data.arguments.execute.inArguments.find(item => item.channelCode)
        ),
        JourneyBuilder: {
          ...state.JourneyBuilder,
          ...getValuesFromPayload(
            payload.data,
            state.JourneyBuilder.timezone,
            state.isSocialMessage
          ),
        },
      };
    case journeyBuilderGetLocalizationActionTypes.request.type:
      return {
        ...state,
        isLoadingLocalizationData: true,
      };
    case journeyBuilderGetLocalizationActionTypes.success.type:
      return {
        ...state,
        isLoadingLocalizationData: false,
        localeInfo: payload,
      };
    case journeyBuilderGetLocalizationActionTypes.failure.type:
      return {
        ...state,
        isLoadingLocalizationData: false,
        error: payload,
      };
    case journeyBuilderGetMetadataActionTypes.request.type:
      return {
        ...state,
        isValidatingJourneyMetadata: true,
      };
    case journeyBuilderGetMetadataActionTypes.success.type:
      return {
        ...state,
        isValidatingJourneyMetadata: false,
        journeyHasMetadata: true,
        topicName: (payload && payload.hasValue && payload.value.topicName) || null,
        isHCO: (payload && payload.hasValue && payload.value.isHCO) || false,
      };
    case journeyBuilderGetMetadataActionTypes.failure.type:
      return {
        ...state,
        isValidatingJourneyMetadata: false,
        journeyHasMetadata: false,
        error: payload && payload.status !== 404 ? payload : undefined,
      };
    case journeyBuilderGetHCOEnabledTypes.request.type:
      return {
        ...state,
        isHCOFeatureFlagLoading: true,
      };
    case journeyBuilderGetHCOEnabledTypes.success.type:
      return {
        ...state,
        isHCOFeatureFlagLoading: false,
        isHCOEnabled: payload,
      };
    case journeyBuilderGetHCOEnabledTypes.failure.type:
      return {
        ...state,
        isHCOFeatureFlagLoading: false,
        error: payload && payload.status !== 404 ? payload : undefined,
      };
    case journeyBuilderDecisionSplitGetObjectFieldsActionTypes.request.type:
      return {
        ...state,
        isLoadingDecisionSplitObjectFields: true,
      };
    case journeyBuilderDecisionSplitGetObjectFieldsActionTypes.success.type:
      return {
        ...state,
        isLoadingDecisionSplitObjectFields: false,
        decisionSplitObjects: state.decisionSplitObjects.map(
          x =>
            payload.hasValue && x.id === payload.value.id
              ? { ...x, fields: cleanObjectFields(payload.value.fields) }
              : x
        ),
      };
    case journeyBuilderDecisionSplitGetObjectFieldsActionTypes.failure.type:
      return {
        ...state,
        isLoadingDecisionSplitObjectFields: false,
        error: payload,
      };
    case journeyBuilderDecisionSplitGetDynamicListObjectFieldsActionTypes.request.type:
      return {
        ...state,
        isLoadingDecisionSplitObjectFields: true,
      };
    case journeyBuilderDecisionSplitGetDynamicListObjectFieldsActionTypes.success.type:
      return {
        ...state,
        isLoadingDecisionSplitObjectFields: false,
        decisionSplitObjects: state.decisionSplitObjects.map(x => {
          if (x.id === payload.objectId) {
            if (payload.payload.hasValue) {
              return {
                ...x,
                fields: [
                  ...x.fields.slice(0, getIndex(state, payload) + 1),
                  ...cleanObjectFields(payload.payload.value.fields),
                ],
              };
            }
          }
          return x;
        }),
      };
    case journeyBuilderDecisionSplitGetDynamicListObjectFieldsActionTypes.failure.type:
      return {
        ...state,
        isLoadingDecisionSplitObjectFields: false,
        error: payload,
      };
    case journeyBuilderDecisionSplitGetUserDataActionTypes.request.type:
      return {
        ...state,
        isLoadingDecisionSplitUserData: true,
      };
    case journeyBuilderDecisionSplitGetUserDataActionTypes.success.type:
      return {
        ...state,
        tokens: null,
        isLoadingDecisionSplitUserData: false,
        culture: (payload.hasValue && payload.value.userDetails.locale.localeCode) || 'en-US',
        currentMID: (payload.hasValue && payload.value.organization.id) || 0,
      };
    case journeyBuilderDecisionSplitGetUserDataActionTypes.failure.type:
      return {
        ...state,
        isLoadingDecisionSplitUserData: false,
        error: payload,
      };
    case journeyBuilderDecisionSplitGetObjectsActionTypes.request.type:
      return {
        ...state,
        isLoadingDecisionSplitObjects: true,
      };
    case journeyBuilderDecisionSplitGetObjectsActionTypes.success.type:
      return {
        ...state,
        isLoadingDecisionSplitObjects: false,
        decisionSplitObjects:
          payload.hasValue &&
          payload.value.sort((obj1, obj2) =>
            obj1.name.toUpperCase().localeCompare(obj2.name.toUpperCase())
          ),
      };
    case journeyBuilderDecisionSplitGetObjectsActionTypes.failure.type:
      return {
        ...state,
        isLoadingDecisionSplitObjects: false,
        error: payload,
      };
    case types.JOURNEY_BUILDER_PAYLOAD_RENAME_PATH:
      return {
        ...state,
        payload: {
          ...state.payload,
          outcomes: renamePath(state.payload.outcomes, payload.index, payload.newValue),
        },
      };
    case types.JOURNEY_BUILDER_DECISION_SPLIT_ERROR:
      return {
        ...state,
        error: payload,
      };
    case types.JOURNEY_BUILDER_CLOSE_ERROR_WINDOW:
      return {
        ...state,
        error: null,
      };
    case types.TRANSLATE_OUTCOMES:
      return {
        ...state,
        payload: {
          ...state.payload,
          outcomes: cleanInitialOutcomes(state.payload.outcomes),
        },
      };

    default:
      return state;
  }
};



// WEBPACK FOOTER //
// ./src/JourneyBuilder/reducers/JourneyBuilder.js